import { useMemo, type ReactElement } from 'react'
import { parseAsStringEnum, useQueryState } from 'nuqs'
import { filterArchived, isEmpty, type AppData } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { SimpleGrid, Stack } from '@mantine/core'
import { useConnectFeature, useQueryDisclosure } from '@hooks'
import { useConnectApps, useProfile, useTranslation } from '@front/model'
import { ButtonAdd, NotReady, Page, TAlert } from '@components'
import { CONNECT_ARCHIVE_VIEWS, type ConnectArchiveView } from '../integration/util'
import { ConnectArchiveTabs } from '../integration/ui'
import { ConnectAccessGuard } from '../integration/access'
import { AppCard } from './AppCard'
import { AddApp } from './AddApp'

function ConnectDeveloperPage(): ReactElement {
  const { t } = useTranslation()
  const [isAddOpen, { close: closeAdd, open: openAdd }] = useQueryDisclosure('add')
  const [archiveView, setArchiveView] = useQueryState(
    'view',
    parseAsStringEnum<ConnectArchiveView>([...CONNECT_ARCHIVE_VIEWS]).withDefault('active')
  )
  const { company } = useProfile()
  const appsState = useConnectApps(company?.companyId ? { companyId: company.companyId } : undefined)
  const { milestone } = useConnectFeature()
  const apps = useMemo(() => filterArchived(appsState.data, archiveView === 'archived'), [archiveView, appsState.data])

  return (
    <ConnectAccessGuard>
      <Page>
        <Page.Heading
          isFetching={appsState.isFetching}
          milestone={milestone}
          refetch={appsState.refetch}
          title={t('Developer')}
        >
          <ButtonAdd onClick={openAdd}>{t('New App')}</ButtonAdd>
        </Page.Heading>
        <Page.Content scrollArea>
          <Stack gap='xl'>
            <ConnectArchiveTabs
              activeLabel={t('Current Apps')}
              archivedLabel={t('Archived Apps')}
              onChange={(value): void => {
                void setArchiveView(value)
              }}
              value={archiveView}
            />
            {(appsState.isLoading || appsState.error || !appsState.data) && (
              <NotReady {...appsState} id={t('connect apps')} />
            )}
            <AppContent apps={apps} archiveView={archiveView} />
          </Stack>
        </Page.Content>
      </Page>

      <AddApp opened={isAddOpen} onClose={closeAdd} />
    </ConnectAccessGuard>
  )
}

type AppContentProps = {
  apps: AppData[]
  archiveView: ConnectArchiveView
}

function AppContent({ apps, archiveView }: AppContentProps): ReactElement {
  const { t } = useTranslation()

  if (isEmpty(apps))
    return (
      <TAlert variant='discovery' title={t('No apps available')}>
        {archiveView === 'archived' ? t('No archived Connect apps exist.') : t('No Connect apps exist yet.')}
      </TAlert>
    )

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing='md'>
      {apps.map((app) => (
        <AppCard app={app} key={app.appId} />
      ))}
    </SimpleGrid>
  )
}

export const Route = createFileRoute('/integration/developer/')({
  component: ConnectDeveloperPage,
})
